/** One DR or CR line of a journal entry. Amounts are always positive; the side is `drCr`. */
export interface JournalEntryLine {
  id: number;
  drCr: "debit" | "credit";
  amount: string;
  /** Our logical account (gl_accounts.account_key). */
  accountKey: string | null;
  /** The QuickBooks account actually posted to, after any per-client exception. */
  qbAccountNumber: string | null;
  /** Fully-qualified QuickBooks name, e.g. "Cash (Holding):Stripe (GiftForward) Clearing". */
  qbAccountName: string | null;
}

export interface JournalEntry {
  id: number;
  /** The day the entry BELONGS to — can be back-dated. What the blotter filters on. */
  accountingDate: string;
  /** The day it was actually recorded. Differs from accountingDate on a back-dated correction. */
  asOnDate: string;
  reasonCode: string;
  reasonLabel: string;
  /** EntryType — 0 normal, 1 adjustment, 2 reversal. */
  entryType: number;
  /** JournalEntryStatus — 0 draft, 1 posted, 2 needs review. */
  status: number;
  /** Allocated at post time — every entry has one. */
  journalNo: string;
  memo: string | null;
  organizationName: string | null;
  groupName: string | null;
  sourceType: number | null;
  sourceId: number | null;
  /** Who caused the entry. Null = the system (a Stripe webhook or a cron). */
  actorName: string | null;
  totalDebit: string;
  totalCredit: string;
  lines: JournalEntryLine[];
  createdAt: string;
}

export interface JournalEntryListQuery {
  /** What the entry was raised for. A type alone answers "all invoice entries"; with an id it
   *  narrows to one invoice or purchase. */
  sourceType?: number;
  sourceId?: number;
  /** Inclusive YYYY-MM-DD bounds on the accounting date. */
  from?: string;
  to?: string;
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface JournalEntryListResponse {
  data: JournalEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** A journal entry as the show page needs it — the list shape plus its resolved source. */
export interface JournalEntryDetail extends JournalEntry {
  /** e.g. an invoice number. Null when the source row is gone or the type carries no reference. */
  sourceReference: string | null;
  /** The group the source belongs to — needed to open the invoice detail. */
  sourceGroupId: number | null;
}

/** An account a hand-keyed entry may post to. */
export interface PostableAccount {
  accountKey: string;
  qbAccountNumber: string | null;
  qbAccountName: string | null;
  accountType: string;
}

/** One live per-client override of where a key posts — shown when a mapper row is expanded. */
export interface AccountMappingException {
  organizationName: string | null;
  groupName: string | null;
  reasonCode: string | null;
  qbAccountNumber: string;
  qbAccountName: string | null;
  effectiveFrom: string;
  effectiveTill: string | null;
  note: string | null;
  resolves: boolean;
}

/** One row of the Chart of Account Mapper. `resolves` false = the bound number isn't in the COA, so
 *  postings quarantine as Needs Review; `exceptions` = clients diverted off the default account. */
export interface AccountMappingRow {
  accountKey: string;
  description: string | null;
  qbAccountNumber: string | null;
  qbAccountName: string | null;
  accountType: string;
  isActive: boolean;
  resolves: boolean;
  exceptions: AccountMappingException[];
}

/** The mapper plus the COA version its names were resolved against. */
export interface AccountMappingView {
  coaVersionId: number | null;
  coaVersionLabel: string | null;
  accounts: AccountMappingRow[];
}

/** One free-form line being composed in the New Journal Entry screen. */
export interface DraftJournalLine {
  /** Local key so React can track rows as they're added and removed. */
  key: string;
  accountKey: string;
  drCr: "debit" | "credit";
  amount: string;
}

export interface CreateJournalEntryPayload {
  accountingDate: string;
  postingDate?: string;
  sourceType?: number;
  sourceId?: number;
  memo?: string;
  lines: { accountKey: string; drCr: "debit" | "credit"; amount: number }[];
}

/** accounting_periods.status */
export const PERIOD_STATUS = { open: 0, closed: 1 } as const;

export interface AccountingPeriod {
  id: number;
  /** First day of the month — the period's identity and its reference. */
  period: string;
  /** Inclusive display bounds, e.g. "Aug 1, 2026 - Aug 31, 2026". */
  periodFrom: string;
  periodTo: string;
  status: number;
  totalDebit: string;
  totalCredit: string;
  entryCount: number;
  /** Entries still held for review — excluded from every export. */
  heldCount: number;
  closedAt: string | null;
  closedByName: string | null;
  reopenedAt: string | null;
}

/** An entry held back from every export until its account mapping resolves. */
export interface NeedsReviewEntry {
  id: number;
  journalNo: string;
  accountingDate: string;
  reasonCode: string;
  reasonLabel: string;
  memo: string | null;
  organizationName: string | null;
  groupName: string | null;
  totalDebit: string;
  /** The account keys that couldn't be matched — what has to be fixed. */
  unresolvedAccounts: string[];
  createdAt: string;
}

export interface RetryResult {
  resolved: boolean;
  unresolved: string[];
}

/** An entry excluded from the export until its account resolves. */
export interface HeldEntry {
  journalNo: string;
  reasonCode: string;
  accountingDate: string;
  memo: string | null;
}

/** Entries in this period whose original date fell in an earlier, closed one. */
export interface CarriedInGroup {
  fromPeriod: string;
  count: number;
  total: string;
}

export interface ExportPreview {
  period: string;
  periodClosed: boolean;
  /** An earlier period still open — blocks posting and closing this one. */
  blockedByPeriod: string | null;
  /** False when this is the newest period; closing it would leave nothing open. */
  hasLaterPeriod: boolean;
  exportableCount: number;
  totalDebit: string;
  totalCredit: string;
  /** The one that matters: these are LEFT OUT of the file, so exporting with any outstanding
   *  sends a month that balances, imports, and is short. */
  heldForReview: HeldEntry[];
  carriedIn: CarriedInGroup[];
}

export interface PostingBatch {
  id: number;
  period: string | null;
  status: number;
  entryCount: number;
  totalDebit: string | null;
  totalCredit: string | null;
  /** When the batch was posted and its file produced. */
  createdAt: string;
  postedAt: string | null;
}

export interface CreatedBatch {
  batchId: number;
  period: string;
  entryCount: number;
  totalDebit: string;
  totalCredit: string;
  csv: string;
  carriedIn: CarriedInGroup[];
}

/** One line of a period's activity feed. */
export interface PeriodEvent {
  id: number;
  eventType: string;
  /** The rendered line, frozen at write time. */
  statusDescription: string;
  /** Null = the system did it (a webhook or a cron). */
  actorName: string | null;
  createdAt: string;
}

/** A journal entry as shown on an invoice or purchase — narrower than the blotter's row. */
export interface RelatedJournalEntry {
  id: number;
  journalNo: string;
  accountingDate: string;
  reasonCode: string;
  reasonLabel: string;
  entryType: number;
  status: number;
  memo: string | null;
  actorName: string | null;
  totalDebit: string;
}
